import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsersData } from '../data/users';
import { fetchUserAttributes } from '../services/userAttributesService';
import { useNotification } from '../components/NotificationContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import useTranslation from '../services/translationService';
import Loading from '../components/Loading';
import { isBreakglassModeActive } from '../services/breakglassService';
import {
  MagnifyingGlassIcon,
  ShieldExclamationIcon,
  UserGroupIcon,
  GlobeAsiaAustraliaIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ComputerDesktopIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const USER_TYPES = {
  SUPER_AGENT: 'super-agent',
  AGENT: 'agent',
  PLAYER: 'player'
};

const COLOR_VARIANTS = {
  default: {
    accentBorder: 'border-gray-200 dark:border-gray-700',
    accentBackground: 'bg-gray-50 dark:bg-gray-900/20',
    accentBar: 'border-gray-300 dark:border-gray-600',
    chip: 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
    playerTag: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
    icon: 'text-gray-500'
  },
  blue: {
    accentBorder: 'border-blue-200 dark:border-blue-800',
    accentBackground: 'bg-blue-50 dark:bg-blue-900/10',
    accentBar: 'border-blue-500 dark:border-blue-400',
    chip: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    playerTag: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    icon: 'text-blue-500'
  },
  purple: {
    accentBorder: 'border-purple-200 dark:border-purple-800',
    accentBackground: 'bg-purple-50 dark:bg-purple-900/10',
    accentBar: 'border-purple-500 dark:border-purple-400',
    chip: 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
    playerTag: 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
    icon: 'text-purple-500'
  },
  green: {
    accentBorder: 'border-green-200 dark:border-green-800',
    accentBackground: 'bg-green-50 dark:bg-green-900/10',
    accentBar: 'border-green-500 dark:border-green-400',
    chip: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300',
    playerTag: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300',
    icon: 'text-green-500'
  },
  yellow: {
    accentBorder: 'border-yellow-200 dark:border-yellow-800',
    accentBackground: 'bg-yellow-50 dark:bg-yellow-900/10',
    accentBar: 'border-yellow-500 dark:border-yellow-400',
    chip: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300',
    playerTag: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300',
    icon: 'text-yellow-500'
  },
  pink: {
    accentBorder: 'border-pink-200 dark:border-pink-800',
    accentBackground: 'bg-pink-50 dark:bg-pink-900/10',
    accentBar: 'border-pink-500 dark:border-pink-400',
    chip: 'bg-pink-100 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300',
    playerTag: 'bg-pink-100 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300',
    icon: 'text-pink-500'
  },
  indigo: {
    accentBorder: 'border-indigo-200 dark:border-indigo-800',
    accentBackground: 'bg-indigo-50 dark:bg-indigo-900/10',
    accentBar: 'border-indigo-500 dark:border-indigo-400',
    chip: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300',
    playerTag: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300',
    icon: 'text-indigo-500'
  },
  red: {
    accentBorder: 'border-red-200 dark:border-red-800',
    accentBackground: 'bg-red-50 dark:bg-red-900/10',
    accentBar: 'border-red-500 dark:border-red-400',
    chip: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300',
    playerTag: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300',
    icon: 'text-red-500'
  },
  orange: {
    accentBorder: 'border-orange-200 dark:border-orange-800',
    accentBackground: 'bg-orange-50 dark:bg-orange-900/10',
    accentBar: 'border-orange-500 dark:border-orange-400',
    chip: 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
    playerTag: 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
    icon: 'text-orange-500'
  },
  teal: {
    accentBorder: 'border-teal-200 dark:border-teal-800',
    accentBackground: 'bg-teal-50 dark:bg-teal-900/10',
    accentBar: 'border-teal-500 dark:border-teal-400',
    chip: 'bg-teal-100 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300',
    playerTag: 'bg-teal-100 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300',
    icon: 'text-teal-500'
  },
  cyan: {
    accentBorder: 'border-cyan-200 dark:border-cyan-800',
    accentBackground: 'bg-cyan-50 dark:bg-cyan-900/10',
    accentBar: 'border-cyan-500 dark:border-cyan-400',
    chip: 'bg-cyan-100 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300',
    playerTag: 'bg-cyan-100 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300',
    icon: 'text-cyan-500'
  }
};

const getColorStyles = (color) => COLOR_VARIANTS[color] || COLOR_VARIANTS.default;

const Monitoring = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [ipConflicts, setIpConflicts] = useState([]);
  const [analyzedUsers, setAnalyzedUsers] = useState([]);
  const [expandedConflicts, setExpandedConflicts] = useState({});
  const [isIOS, setIsIOS] = useState(false);
  const [isBreakglassActive, setIsBreakglassActive] = useState(false);

  const usersMap = useMemo(() => {
    return allUsers.reduce((acc, user) => {
      if (user && user.username) {
        acc[user.username] = user;
      }
      return acc;
    }, {});
  }, [allUsers]);
  
  // Color palette for different agents
  const agentColors = [
    'blue', 'purple', 'green', 'yellow', 'pink', 'indigo', 'red', 'orange', 'teal', 'cyan'
  ];
  const agentColorMap = new Map();

  // Check for breakglass mode
  useEffect(() => {
    const checkBreakglass = async () => {
      const breakglassActive = await isBreakglassModeActive();
      setIsBreakglassActive(breakglassActive);
    };
    checkBreakglass();
  }, []);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const userGroups = tokens?.accessToken?.payload['cognito:groups'] || [];
        const hasAdminAccess = userGroups.includes('Admins') || userGroups.includes("Countries");
        
        if (!hasAdminAccess) {
          showNotification(t('monitoring.accessDenied', 'Access denied. Admin privileges required.'), 'error');
          navigate('/');
          return;
        }
        
        setIsAdmin(true);
        await loadUsers();
      } catch (error) {
        console.error('Error checking admin status:', error);
        showNotification(t('monitoring.errors.checkingAuth', 'Error verifying permissions'), 'error');
        navigate('/');
      }
    };

    checkAdminStatus();
  }, [navigate, showNotification, t]);

  // Detect iOS device
  useEffect(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);
  }, []);

  const loadUsers = async () => {
    try {
      const { success, data } = await getUsersData();
      if (success && data && data.users) {
        const users = Object.values(data.users);
        setAllUsers(users);
        
        // Filter for super-agents only (cities)
        const superAgents = users.filter(u => 
          u.type === USER_TYPES.SUPER_AGENT
        );
        setFilteredAgents(superAgents);
        setSearchResults(superAgents.slice(0, 20)); // Show first 20 by default
        console.log(`Loaded ${users.length} total users, found ${superAgents.length} super-agents`);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showNotification(t('monitoring.errors.loadingUsers', 'Error loading users'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    
    if (!value.trim()) {
      setSearchResults(filteredAgents.slice(0, 20));
      return;
    }

    const searchLower = value.toLowerCase();
    const filtered = filteredAgents.filter(agent => {
      const matchesSearch = (
        agent.username?.toLowerCase().includes(searchLower) ||
        agent.name?.toLowerCase().includes(searchLower) ||
        agent.type?.toLowerCase().includes(searchLower)
      );
      
      return matchesSearch;
    });
    
    setSearchResults(filtered.slice(0, 50)); // Limit to 50 results
  }, [filteredAgents]);

  // Helper function to fetch attributes in batches
  const fetchAttributesInBatches = async (users, batchSize = 25) => {
    // Pre-filter users to ensure we only process valid ones
    const validUsers = users.filter(user => {
      const isValid = user.username && 
                     typeof user.username === 'string' &&
                     !(/^\d+$/.test(user.username)) && // Not purely numeric
                     !(/^test/i.test(user.username)) && // Not test account
                     user.username.length > 2;
      
      if (!isValid && user.username) {
        console.warn(`Skipping attribute fetch for invalid user: ${user.username}`);
      }
      return isValid;
    });
    
    console.log(`Fetching attributes for ${validUsers.length} valid users (filtered from ${users.length} total)`);
    
    const results = [];
    for (let i = 0; i < validUsers.length; i += batchSize) {
      const batch = validUsers.slice(i, i + batchSize);
      const batchNumber = Math.floor(i / batchSize) + 1;
      const totalBatches = Math.ceil(validUsers.length / batchSize);
      
      console.log(`Processing batch ${batchNumber} of ${totalBatches} (${batch.length} users)`);
      
      const batchPromises = batch.map(async (user) => {
        try {
          // Only log every 10th user to reduce console spam
          if (batch.indexOf(user) % 10 === 0) {
            console.log(`Fetching attributes for user: ${user.username} (${user.type})`);
          }
          const attributes = await fetchUserAttributes(user.username);
          if (attributes?.lastIPAddress && attributes.lastIPAddress !== 'unknown') {
            return {
              ...user,
              attributes,
              ip: attributes.lastIPAddress
            };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching attributes for ${user.username}:`, error);
          return null;
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults.filter(r => r !== null));
      
      // Log progress to console only - no notifications during fetching
      const processedCount = Math.min(i + batchSize, validUsers.length);
      console.log(`Progress: ${processedCount}/${validUsers.length} users fetched`);
      
      // Add progressive delay between batches to avoid rate limiting (except for the last batch)
      if (i + batchSize < validUsers.length) {
        // Progressive delay: starts at 500ms and increases by 100ms per batch, capped at 2000ms
        const delayMs = Math.min(500 + (batchNumber * 100), 2000);
        console.log(`Waiting ${delayMs}ms before next batch to avoid rate limiting...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
    
    console.log(`Completed fetching attributes for ${results.length} users with valid IPs`);
    return results;
  };

  const analyzeAgent = async (agent) => {
    setSelectedAgent(agent);
    setIsAnalyzing(true);
    setIpConflicts([]);
    setAnalyzedUsers([]);
    
    // Clear the agent color map for new analysis
    agentColorMap.clear();

    try {
      // Build the tree structure from flat data
      const buildTreeFromFlat = (username, usersMap) => {
        const user = usersMap[username];
        if (!user) return null;
        
        const node = {
          ...user,
          children: []
        };
        
        // Recursively build children
        if (user.children && user.children.length > 0) {
          user.children.forEach(childUsername => {
            const childNode = buildTreeFromFlat(childUsername, usersMap);
            if (childNode) {
              node.children.push(childNode);
            }
          });
        }
        
        return node;
      };
      
      // Get the users map from the loaded data
      const { success, data } = await getUsersData();
      if (!success || !data || !data.users) {
        showNotification(t('monitoring.errors.loadingUsers', 'Error loading users'), 'error');
        setIsAnalyzing(false);
        return;
      }
      
      // Build the tree for the selected agent
      const agentNode = buildTreeFromFlat(agent.username, data.users);
      if (!agentNode) {
        showNotification(t('monitoring.errors.agentNotFound', 'Agent not found'), 'error');
        setIsAnalyzing(false);
        return;
      }
      
      // Get all users (agents and players) under this agent hierarchy
      const usersUnderAgent = getAllUsersUnderNode(agentNode);
      
      console.log(`Found ${usersUnderAgent.length} users (agents and players) under ${agent.username}`);
      
      // Only proceed if there are users to analyze
      if (usersUnderAgent.length === 0) {
        showNotification(
          t('monitoring.noUsers', 'No users found under this agent'),
          'warning'
        );
        setIsAnalyzing(false);
        return;
      }
      
      // Log analysis start
      console.log(`Starting analysis of ${usersUnderAgent.length} users under ${agent.name || agent.username}`);

      // Fetch attributes for all users under this agent in batches
      const usersWithAttributes = await fetchAttributesInBatches(usersUnderAgent);
      
      console.log(`Fetched attributes for ${usersWithAttributes.length} users with valid IPs`);
      
      // Check if we got any valid data
      if (usersWithAttributes.length === 0) {
        showNotification(
          t('monitoring.noValidIPs', 'No users with valid IP addresses found'),
          'warning'
        );
        setIsAnalyzing(false);
        setAnalyzedUsers([]);
        return;
      }
      
      // Build IP map from users under this agent
      console.log('Building IP conflict map...');
      const ipToUsersUnderAgent = new Map();
      
      try {
        usersWithAttributes.forEach(user => {
          if (user && user.ip) {
            if (!ipToUsersUnderAgent.has(user.ip)) {
              ipToUsersUnderAgent.set(user.ip, []);
            }
            ipToUsersUnderAgent.get(user.ip).push(user);
          }
        });
        
        console.log(`Found ${ipToUsersUnderAgent.size} unique IP addresses`);
        
        // Find IPs that have multiple users from DIFFERENT agents
        const crossAgentConflicts = [];
        ipToUsersUnderAgent.forEach((users, ip) => {
          if (users.length > 1) {
            // Group users by their direct agent parent
            const usersByAgent = new Map();
            users.forEach(user => {
              // Find the direct agent parent for this user
              let current = user;
              let agent = null;
              const visited = new Set();
              
              while (current && !visited.has(current.username)) {
                visited.add(current.username);
                if (current.type === USER_TYPES.AGENT || current.type === USER_TYPES.SUPER_AGENT) {
                  agent = current;
                  break;
                }
                if (!current.parentUsername) break;
                current = usersMap[current.parentUsername];
              }
              
              if (agent) {
                const agentKey = agent.username;
                if (!usersByAgent.has(agentKey)) {
                  usersByAgent.set(agentKey, []);
                }
                usersByAgent.get(agentKey).push(user);
              }
            });
            
            // Only add conflict if users are from different agents
            if (usersByAgent.size > 1) {
              crossAgentConflicts.push({
                ip,
                users,
                type: 'cross-agent',
                agentCount: usersByAgent.size
              });
            }
          }
        });
        
        console.log(`Found ${crossAgentConflicts.length} cross-agent IP conflicts`);
        
        // Convert conflicts to the standard format
        const allConflicts = crossAgentConflicts.map(conflict => ({
          ip: conflict.ip,
          mainUser: conflict.users[0],
          conflictingUsers: conflict.users.slice(1),
          type: 'cross-agent',
          timestamp: new Date().toISOString()
        }));

        // Update state with results
        setAnalyzedUsers(usersWithAttributes);
        setIpConflicts(allConflicts);
        
        // Show final result
        if (allConflicts.length > 0) {
          showNotification(
            t('monitoring.conflictsFound', {
              count: allConflicts.length
            }, `Found ${allConflicts.length} IP conflicts!`),
            'warning'
          );
        } else {
          showNotification(
            t('monitoring.noConflicts', 'No IP conflicts found'),
            'success'
          );
        }
        
        console.log('Analysis complete');
        
      } catch (mapError) {
        console.error('Error processing IP conflicts:', mapError);
        showNotification(t('monitoring.errors.processingConflicts', 'Error processing IP conflicts'), 'error');
        setAnalyzedUsers([]);
        setIpConflicts([]);
      } finally {
        setIsAnalyzing(false);
      }
      
    } catch (error) {
      console.error('Error analyzing agent:', error);
      showNotification(t('monitoring.errors.analyzing', 'Error analyzing agent'), 'error');
      setIsAnalyzing(false);
      setAnalyzedUsers([]);
      setIpConflicts([]);
    }
  };

  // Helper function to get all users under a node (including agents and players)
  const getAllUsersUnderNode = (node) => {
    const users = [];
    
    const traverse = (currentNode) => {
      if (!currentNode) return;
      
      // Skip the root node itself, we only want descendants
      if (currentNode !== node) {
        // Validate the username isn't numeric or test
        const isValidUsername = currentNode.username && 
                               typeof currentNode.username === 'string' &&
                               !(/^\d+$/.test(currentNode.username)) && // Not purely numeric
                               !(/^test/i.test(currentNode.username)) && // Not test account
                               currentNode.username.length > 2;
        
        if (isValidUsername) {
          users.push({
            username: currentNode.username,
            name: currentNode.name,
            attributes: currentNode.attributes || {},
            balances: currentNode.balances || {},
            parentUsername: currentNode.parentUsername,
            type: currentNode.type
          });
        }
      }
      
      // Traverse children
      if (currentNode.children && currentNode.children.length > 0) {
        currentNode.children.forEach(child => traverse(child));
      }
    };
    
    traverse(node);
    return users;
  };

  // Helper function to get only players under a node
  const getAllPlayersUnderNode = (node) => {
    return getAllUsersUnderNode(node).filter(user => user.type === 'player');
  };

  // Get the agent chain for a user
  const getAgentChain = (user, allUsersList) => {
    const chain = [];
    let current = user;
    
    while (current) {
      chain.unshift(current);
      if (!current.parentUsername) break;
      current = allUsersList.find(u => u.username === current.parentUsername);
    }
    
    return chain;
  };
  
  // Get the direct agent parent for a user
  const getDirectAgent = (user, usersMap) => {
    if (!user) return null;
    
    let current = user;
    const visited = new Set();
    const maxDepth = 10; // Prevent infinite loops
    let depth = 0;
    
    while (current && !visited.has(current.username) && depth < maxDepth) {
      visited.add(current.username);
      depth++;
      
      // If this is an agent or super-agent, return it
      if (current.type === USER_TYPES.AGENT || current.type === USER_TYPES.SUPER_AGENT) {
        return current;
      }
      
      // Move up to parent
      if (!current.parentUsername) break;
      
      // Only look in usersMap to avoid expensive array search
      current = usersMap[current.parentUsername];
      if (!current) break;
    }
    
    return null;
  };
  
  // Get color for agent
  const getAgentColor = (agentUsername) => {
    if (!agentColorMap.has(agentUsername)) {
      const colorIndex = agentColorMap.size % agentColors.length;
      agentColorMap.set(agentUsername, agentColors[colorIndex]);
    }
    return agentColorMap.get(agentUsername);
  };

  const toggleConflictExpansion = (ip) => {
    setExpandedConflicts(prev => ({
      ...prev,
      [ip]: !prev[ip]
    }));
  };

  if (isLoading) {
    return <Loading message={t('monitoring.loading', 'Loading monitoring data...')} />;
  }

  if (!isAdmin) {
    return null; // Will redirect in useEffect
  }

  // Show breakglass warning if active
  if (isBreakglassActive) {
    return (
      <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${isIOS ? 'pb-safe' : 'pb-4'}`}>
        <div className={`sticky top-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 ${isIOS ? 'pt-safe' : ''}`}>
          <div className="px-4 py-4">
            <div className="flex items-center gap-3">
              <ShieldExclamationIcon className="w-6 h-6 text-red-500" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('monitoring.title', 'Cross-Agent IP Monitoring')}
              </h1>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 dark:border-yellow-700 rounded-xl p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <svg className="w-16 h-16 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {t('monitoring.breakglassMode', 'Breakglass Mode Active')}
                </h2>
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  {t('monitoring.breakglassModeDescription', 'IP monitoring is temporarily disabled for security reasons.')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${isIOS ? 'pb-safe' : 'pb-4'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 
        ${isIOS ? 'pt-safe' : ''}`}>
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <ShieldExclamationIcon className="w-6 h-6 text-red-500" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('monitoring.title', 'Cross-Agent IP Monitoring')}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Search & Guidance Section */}
        <div className="space-y-4">
          <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 flex flex-col gap-4 ${isIOS ? 'rounded-[28px]' : 'rounded-xl'}`}>
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder={t('monitoring.searchPlaceholder', 'Search cities...')}
                  className="w-full px-4 py-3 pl-12 pr-10 rounded-xl border border-gray-200 dark:border-gray-600 
                    bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm sm:text-base
                    focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                    placeholder-gray-400 dark:placeholder-gray-500 shadow-inner"
                />
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      handleSearch('');
                    }}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full
                      hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors
                      ${isIOS ? 'active:scale-95' : ''}`}
                  >
                    <XMarkIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </button>
                )}
              </div>

              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {searchResults.map((agent) => (
                    <button
                      key={agent.username}
                      onClick={() => analyzeAgent(agent)}
                      disabled={isAnalyzing}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left
                        ${selectedAgent?.username === agent.username
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                          : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700'
                        }
                        hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600
                        disabled:opacity-50 disabled:cursor-not-allowed
                        ${isIOS ? 'active:scale-95' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                          <GlobeAsiaAustraliaIcon className="w-5 h-5 text-orange-500" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">
                            {agent.name || agent.username}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            @{agent.username}
                          </p>
                        </div>
                      </div>
                      <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Selected Agent Info */}
        {selectedAgent && (
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4
            ${isIOS ? 'rounded-2xl' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/20">
                  <GlobeAsiaAustraliaIcon className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {selectedAgent.name || selectedAgent.username}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {analyzedUsers.length} users • {ipConflicts.length} cross-agent conflicts
                  </p>
                </div>
              </div>
              {!isAnalyzing && (
                <button
                  onClick={() => analyzeAgent(selectedAgent)}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg
                    bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300
                    hover:bg-blue-200 dark:hover:bg-blue-900/30 transition-colors
                    ${isIOS ? 'active:scale-95' : ''}`}
                >
                  <ArrowPathIcon className="w-4 h-4 mr-1" />
                  {t('monitoring.refresh', 'Refresh')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isAnalyzing && (
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8
            ${isIOS ? 'rounded-2xl' : ''}`}>
            <div className="flex flex-col items-center justify-center">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-800 rounded-full"></div>
                <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-500 rounded-full animate-spin border-t-transparent"></div>
              </div>
              <p className="mt-4 text-gray-600 dark:text-gray-400 text-center">
                {t('monitoring.analyzing', 'Analyzing IP addresses...')}
              </p>
            </div>
          </div>
        )}

        {!isAnalyzing && selectedAgent && (
          <>
            {ipConflicts.length > 0 && (
              <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm
                ${isIOS ? 'rounded-2xl' : ''}`}>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {ipConflicts.map((conflict) => {
                    const conflictInfo = buildConflictInfo(conflict, usersMap, getDirectAgent, getAgentColor);
                    return (
                      <ConflictRow
                        key={conflict.ip}
                        conflict={conflict}
                        conflictInfo={conflictInfo}
                        expanded={!!expandedConflicts[conflict.ip]}
                        toggleExpansion={toggleConflictExpansion}
                        usersMap={usersMap}
                        isIOS={isIOS}
                        navigate={navigate}
                        t={t}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {ipConflicts.length === 0 && analyzedUsers.length > 0 && (
              <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8
                ${isIOS ? 'rounded-2xl' : ''}`}>
                <div className="flex flex-col items-center justify-center text-center">
                  <CheckCircleIcon className="w-12 h-12 text-green-500 mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {t('monitoring.noConflictsTitle', 'No Cross-Agent Conflicts')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('monitoring.noConflictsDescription', 
                      'No players from different agents are sharing IP addresses.'
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Helper component for user conflict cards
const UserConflictCard = ({ user, usersMap, navigate, isIOS, agentColor }) => {
  const { t } = useTranslation();
  const agentChain = getAgentChainDisplay(user, usersMap);
  const { accentBorder, accentBackground, chip, icon, playerTag } = getColorStyles(agentColor);

  return (
    <div className={`border rounded-lg p-3 transition-all ${accentBorder} ${accentBackground}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <UserIcon className={`w-4 h-4 ${icon}`} />
            <span className="font-medium text-gray-900 dark:text-white">
              {user.name || user.username}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${playerTag}`}>
              @{user.username}
            </span>
            {user.type && (
              <span className={`text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full ${chip}`}>
                {user.type === USER_TYPES.PLAYER ? t('userTypes.player', 'Player') : user.type}
              </span>
            )}
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400">
            {agentChain}
          </div>
        </div>

        <button
          onClick={() => navigate(`/users/${user.username}/deep-view`)}
          className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors
            bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700
            border border-gray-300 dark:border-gray-600
            ${isIOS ? 'active:scale-95' : ''}`}
        >
          {t('common.view', 'View')}
        </button>
      </div>
    </div>
  );
};

// Helper function to get agent name
// Helper function to get agent chain display
const getAgentChainDisplay = (user, usersMap) => {
  let current = user;
  const chain = [];
  const visited = new Set();
  
  while (current && !visited.has(current.username)) {
    visited.add(current.username);
    chain.unshift(`${current.name || current.username} (${current.type})`);
    if (!current.parentUsername) break;
    current = usersMap[current.parentUsername];
  }
  
  return chain.join(' → ');
};

const buildConflictInfo = (conflict, usersMap, getDirectAgent, getAgentColor) => {
  const allUsers = [conflict.mainUser, ...conflict.conflictingUsers].filter(Boolean);
  const agentBuckets = new Map();

  allUsers.forEach((user) => {
    const agent = getDirectAgent(user, usersMap);
    if (!agent) return;

    if (!agentBuckets.has(agent.username)) {
      agentBuckets.set(agent.username, {
        agent,
        color: getAgentColor(agent.username),
        users: []
      });
    }

    agentBuckets.get(agent.username).users.push({
      ...user,
      agent
    });
  });

  const uniqueAgents = Array.from(agentBuckets.values());

  return {
    totalUsers: allUsers.length,
    uniqueAgents,
    hasCrossAgent: uniqueAgents.length > 1,
    agentBuckets
  };
};

const ConflictRow = ({ conflict, conflictInfo, expanded, toggleExpansion, usersMap, isIOS, navigate, t }) => {
  if (conflictInfo.totalUsers === 0) {
    return null;
  }

  return (
    <div className="p-4 transition-colors">
      <button
        onClick={() => toggleExpansion(conflict.ip)}
        className={`w-full flex items-start gap-3 text-left ${isIOS ? 'active:scale-95' : ''}`}
      >
        <div className="flex-shrink-0 mt-1">
          {expanded ? (
            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRightIcon className="w-5 h-5 text-gray-500" />
          )}
        </div>

        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComputerDesktopIcon
              className={`w-5 h-5 ${conflictInfo.hasCrossAgent ? 'text-red-500' : 'text-amber-500'}`}
            />
            <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
              {conflict.ip}
            </span>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                conflictInfo.hasCrossAgent
                  ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                  : 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
              }`}
            >
              {conflictInfo.totalUsers} {t('common.users', 'users')}
            </span>
            {conflictInfo.uniqueAgents.length > 1 && (
              <span className="px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-xs font-medium">
                {conflictInfo.uniqueAgents.length} {t('common.agents', 'agents')}
              </span>
            )}
          </div>

          <AgentDistribution conflictInfo={conflictInfo} t={t} />
        </div>
      </button>

      {expanded && (
        <ConflictDetails
          conflictInfo={conflictInfo}
          usersMap={usersMap}
          navigate={navigate}
          isIOS={isIOS}
          ip={conflict.ip}
        />
      )}
    </div>
  );
};

const AgentDistribution = ({ conflictInfo, t }) => {
  const showChips = conflictInfo.uniqueAgents.length <= 4;

  if (showChips) {
    return (
      <div className="flex flex-wrap gap-2">
        {conflictInfo.uniqueAgents.map(({ agent, color, users }) => {
          const { chip, accentBorder } = getColorStyles(color);
          return (
            <span
              key={agent.username}
              className={`flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full border ${chip} ${accentBorder}`}
            >
              <UserGroupIcon className="w-3 h-3" />
              <span>{agent.name || agent.username}</span>
              <span className="text-[10px] tracking-wide">• {users.length} {t('common.users', 'users')}</span>
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {conflictInfo.uniqueAgents.slice(0, 4).map(({ agent, color, users }) => {
        const { accentBackground, accentBorder } = getColorStyles(color);
        return (
          <div key={agent.username} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${accentBackground} ${accentBorder}`}>
            <span className="font-medium text-xs text-gray-900 dark:text-gray-200">
              {agent.name || agent.username}
            </span>
            <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className={`h-full ${getBarColor(color)}`}
                style={{ width: `${(users.length / conflictInfo.totalUsers) * 100}%` }}
              />
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-400">{users.length}</span>
          </div>
        );
      })}
      {conflictInfo.uniqueAgents.length > 4 && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          +{conflictInfo.uniqueAgents.length - 4}
        </span>
      )}
    </div>
  );
};

const ConflictDetails = ({ conflictInfo, usersMap, navigate, isIOS, ip }) => {
  const { t } = useTranslation();
  
  // Get all user names with their types
  const allUsers = [];
  conflictInfo.uniqueAgents.forEach(({ users }) => {
    users.forEach(user => {
      allUsers.push({
        name: user.name || user.username,
        type: user.type
      });
    });
  });
  
  // Format the user list - show ALL names with types
  const formatUserList = () => {
    const formatUser = (user) => {
      let typeLabel = '';
      if (user.type === USER_TYPES.PLAYER) {
        typeLabel = t('userTypes.player', 'player');
      } else if (user.type === USER_TYPES.AGENT) {
        typeLabel = t('userTypes.agent', 'agent');
      } else if (user.type === USER_TYPES.SUPER_AGENT) {
        typeLabel = t('userTypes.super-agent', 'super-agent');
      } else {
        typeLabel = user.type || 'user';
      }
      return `${user.name} (${typeLabel})`;
    };
    
    if (allUsers.length === 1) {
      return formatUser(allUsers[0]);
    } else if (allUsers.length === 2) {
      return `${formatUser(allUsers[0])} and ${formatUser(allUsers[1])}`;
    } else {
      const lastUser = allUsers[allUsers.length - 1];
      const otherUsers = allUsers.slice(0, -1).map(formatUser).join(', ');
      return `${otherUsers}, and ${formatUser(lastUser)}`;
    }
  };
  
  return (
    <div className="mt-4 ml-8 space-y-3">
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
        <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 dark:text-amber-200">
          <span className="font-medium">{formatUserList()}</span> {t('monitoring.sharingIP', 'are all sharing IP')} <span className="font-mono">{ip}</span>
        </p>
      </div>
      <UserConflictList
        conflictInfo={conflictInfo}
        usersMap={usersMap}
        navigate={navigate}
        isIOS={isIOS}
      />
    </div>
  );
};

const UserConflictList = ({ conflictInfo, usersMap, navigate, isIOS }) => {
  const orderedAgents = [...conflictInfo.uniqueAgents].sort((a, b) => b.users.length - a.users.length);

  return (
    <div className="space-y-3">
      {orderedAgents.map(({ agent, color, users }) => (
        <div key={agent.username} className="space-y-2">
          <AgentHeader agent={agent} color={color} userCount={users.length} />
          <div className="grid gap-2 lg:grid-cols-2">
            {users.map((user) => (
              <UserConflictCard
                key={user.username}
                user={user}
                usersMap={usersMap}
                navigate={navigate}
                isIOS={isIOS}
                agentColor={color}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const AgentHeader = ({ agent, color, userCount }) => {
  const { chip, accentBorder } = getColorStyles(color);

  return (
    <div className={`flex items-center justify-between text-xs font-medium px-3 py-2 rounded-lg border ${chip} ${accentBorder}`}>
      <span className="flex items-center gap-1">
        <UserGroupIcon className="w-4 h-4" />
        {agent.name || agent.username}
      </span>
      <span>{userCount}</span>
    </div>
  );
};

const getBarColor = (color) => {
  switch (color) {
    case 'blue':
      return 'bg-blue-400 dark:bg-blue-500';
    case 'purple':
      return 'bg-purple-400 dark:bg-purple-500';
    case 'green':
      return 'bg-green-400 dark:bg-green-500';
    case 'yellow':
      return 'bg-yellow-400 dark:bg-yellow-500';
    case 'pink':
      return 'bg-pink-400 dark:bg-pink-500';
    case 'indigo':
      return 'bg-indigo-400 dark:bg-indigo-500';
    case 'red':
      return 'bg-red-400 dark:bg-red-500';
    case 'orange':
      return 'bg-orange-400 dark:bg-orange-500';
    case 'teal':
      return 'bg-teal-400 dark:bg-teal-500';
    case 'cyan':
      return 'bg-cyan-400 dark:bg-cyan-500';
    default:
      return 'bg-gray-400 dark:bg-gray-500';
  }
};

export default Monitoring;
